import React, { useState } from 'react';
import { X, Search, Brain, Database, Sparkles, FileText, ChevronRight, Loader2, Info, Zap, Target, Award, Settings2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { documentStoreService } from '@/services/document-store.service';
import toast from 'react-hot-toast';

interface AIDocumentSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeName: string;
  storeId: string;
}

type TabType = 'enhanced' | 'raw';
type SearchMode = 'fast' | 'balanced' | 'accurate';

export default function AIDocumentSearchModal({
  isOpen,
  onClose,
  storeName,
  storeId,
}: AIDocumentSearchModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('enhanced');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('fast');
  const [topK, setTopK] = useState(10);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    content: string;
    raw_data: string[];
    scores?: number[];
    chunks_count: number;
  } | null>(null);

  const searchModeConfig = {
    fast: {
      label: 'Fast',
      icon: Zap,
      description: '~2s, 80-85% accuracy',
      enable_reranking: false,
      enable_context_expansion: false,
      color: 'green'
    },
    balanced: {
      label: 'Balanced',
      icon: Target,
      description: '~4s, 85-90% accuracy',
      enable_reranking: true,
      enable_context_expansion: false,
      color: 'blue'
    },
    accurate: {
      label: 'Accurate',
      icon: Award,
      description: '~4.5s, 90-95% accuracy',
      enable_reranking: true,
      enable_context_expansion: true,
      color: 'purple'
    }
  };

  const searchMutation = useMutation({
    mutationFn: async (query: string) => {
      const modeConfig = searchModeConfig[searchMode];
      const results = await documentStoreService.searchDocumentContent({
        search_query: query,
        document_store_id: storeId,
        top_k: topK,
        include_scores: true,
        enable_reranking: modeConfig.enable_reranking,
        enable_context_expansion: modeConfig.enable_context_expansion
      });
      return results;
    },
    onSuccess: (data) => {
      setSearchResults({
        content: data.content,
        raw_data: data.raw_data || [],
        scores: data.scores,
        chunks_count: data.chunks_count
      });
      toast.success('Search completed successfully!');
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Search failed. Please try again.'
      );
      setSearchResults(null);
    }
  });

  const handleSearch = () => {
    if (searchQuery.trim()) {
      searchMutation.mutate(searchQuery);
    } else {
      toast.error('Please enter a search query');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      handleSearch();
    }
  };

  const formatSimilarityScore = (score?: number) => {
    if (!score) return 'N/A';
    return `${(score * 100).toFixed(1)}%`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Brain className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">AI-Powered Document Search</h2>
                <p className="text-sm text-gray-600 mt-1">Search through: {storeName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-4 flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Ask anything about your documents... (e.g., 'What are the key features?', 'Find pricing information')"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyPress}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searchMutation.isPending || !searchQuery.trim()}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {searchMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Search
                </>
              )}
            </button>
          </div>

          {/* Search Mode Selector */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Search Mode</label>
              <button
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
              >
                <Settings2 className="w-3 h-3" />
                {showAdvancedOptions ? 'Hide' : 'Show'} Advanced
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {(Object.keys(searchModeConfig) as SearchMode[]).map((mode) => {
                const config = searchModeConfig[mode];
                const IconComponent = config.icon;
                const isActive = searchMode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => setSearchMode(mode)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      isActive
                        ? `border-${config.color}-500 bg-${config.color}-50`
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <IconComponent className={`w-4 h-4 ${isActive ? `text-${config.color}-600` : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${isActive ? `text-${config.color}-900` : 'text-gray-700'}`}>
                        {config.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{config.description}</p>
                  </button>
                );
              })}
            </div>

            {/* Advanced Options */}
            {showAdvancedOptions && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Number of Results (top_k): {topK}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={topK}
                      onChange={(e) => setTopK(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">Range: 1-50 (Default: 10)</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 bg-white rounded border border-gray-200">
                      <span className="font-medium">Re-ranking:</span> {searchModeConfig[searchMode].enable_reranking ? 'Enabled' : 'Disabled'}
                    </div>
                    <div className="p-2 bg-white rounded border border-gray-200">
                      <span className="font-medium">Context Expansion:</span> {searchModeConfig[searchMode].enable_context_expansion ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('enhanced')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors ${
              activeTab === 'enhanced'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Enhanced with LLM
          </button>
          <button
            onClick={() => setActiveTab('raw')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors ${
              activeTab === 'raw'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Database className="w-4 h-4" />
            Raw Vector Data
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {!searchResults && !searchMutation.isPending && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="p-4 bg-gray-100 rounded-full mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Start Your Search</h3>
              <p className="text-sm text-gray-600 max-w-md">
                Enter a question or keyword to search through your document store using AI-powered semantic search
              </p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-left max-w-2xl">
                <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                  <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Example:</p>
                    <p className="text-xs text-gray-600">"What are the main features?"</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                  <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Example:</p>
                    <p className="text-xs text-gray-600">"Find pricing information"</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                  <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Example:</p>
                    <p className="text-xs text-gray-600">"Summarize the key points"</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                  <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Example:</p>
                    <p className="text-xs text-gray-600">"What are the requirements?"</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {searchMutation.isPending && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-4" />
              <p className="text-gray-600">Searching through your documents...</p>
            </div>
          )}

          {searchResults && activeTab === 'enhanced' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">AI-Enhanced Results</h3>
              </div>
              
              {searchResults.content ? (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
                  <div className="prose prose-sm max-w-none">
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {searchResults.content}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-900">No results found</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        Try rephrasing your query or using different keywords
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Tip:</strong> The AI analyzes your documents and provides contextual answers based on the content. 
                  Results are generated using advanced language models for better understanding.
                </p>
              </div>
            </div>
          )}

          {searchResults && activeTab === 'raw' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">Raw Vector Store Results</h3>
                <span className="text-sm text-gray-500">
                  ({searchResults.chunks_count} chunks found)
                </span>
              </div>

              {searchResults.raw_data.length > 0 ? (
                <div className="space-y-3">
                  {searchResults.raw_data.map((chunk, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">
                            Chunk {index + 1}
                          </span>
                        </div>
                        {searchResults.scores && searchResults.scores[index] && (
                          <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                            Score: {formatSimilarityScore(searchResults.scores[index])}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                        <p className="whitespace-pre-wrap">{chunk}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No raw data available</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Raw vector store results will appear here once the API is updated
                  </p>
                </div>
              )}

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> Raw data shows the actual text chunks stored in the vector database 
                  along with their similarity scores. Higher scores indicate better matches to your query.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {searchResults && (
                <p>
                  Query: <span className="font-medium">"{searchQuery}"</span>
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {searchResults && (
                <button
                  onClick={() => {
                    setSearchResults(null);
                    setSearchQuery('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear Results
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}